import { isImageFile, compressImageFile } from '@proaxiv/ui'
import type { AppEvent, UploadedFile } from '../store/eventsSlice'

// ── Font catalogue ────────────────────────────────────────────────────────────

/** A selectable font option offered in the event appearance editor. */
export interface FontOption {
  family: string
  label: string
  /**
   * Google Fonts URL query value.
   * Omit for system fonts (e.g. Meiryo UI) — no stylesheet will be injected.
   */
  query?: string
}

export const CURATED_FONTS: FontOption[] = [
  { family: 'Fraunces', label: 'Fraunces', query: 'Fraunces:wght@300;400;700;900' },
  { family: 'Sora', label: 'Sora', query: 'Sora:wght@300;400;600;700' },
  {
    family: 'Bricolage Grotesque',
    label: 'Bricolage Grotesque',
    query: 'Bricolage+Grotesque:wght@300;400;600;800',
  },
  { family: 'DM Serif Display', label: 'DM Serif Display', query: 'DM+Serif+Display' },
  {
    family: 'Playfair Display',
    label: 'Playfair Display',
    query: 'Playfair+Display:wght@400;700;900',
  },
  { family: 'Bebas Neue', label: 'Bebas Neue', query: 'Bebas+Neue' },
  { family: 'Outfit', label: 'Outfit', query: 'Outfit:wght@300;400;600;700' },
  {
    family: 'Cabinet Grotesk',
    label: 'Cabinet Grotesk',
    query: 'Cabinet+Grotesk:wght@400;500;700;800',
  },
  {
    family: 'Libre Baskerville',
    label: 'Libre Baskerville',
    query: 'Libre+Baskerville:wght@400;700',
  },
  { family: 'Unbounded', label: 'Unbounded', query: 'Unbounded:wght@300;400;700;900' },
  { family: 'Figtree', label: 'Figtree', query: 'Figtree:wght@300;400;600;700;800;900' },
  {
    family: 'Hanken Grotesk',
    label: 'Hanken Grotesk',
    query: 'Hanken+Grotesk:wght@300;400;500;600;700;800;900',
  },
  { family: 'DM Sans', label: 'DM Sans', query: 'DM+Sans:wght@300;400;500;700;900' },
  { family: 'Albert Sans', label: 'Albert Sans', query: 'Albert+Sans:wght@300;400;600;700;900' },
  { family: 'Urbanist', label: 'Urbanist', query: 'Urbanist:wght@300;400;600;700;900' },
  {
    family: 'Google Sans Flex',
    label: 'Google Sans Flex',
    query: 'Google+Sans+Flex:opsz,wght@6..144,1..1000',
  },
  // System font — no Google Fonts download needed, uses the OS installation on Windows
  { family: 'Meiryo UI', label: 'Meiryo UI' },
]

// ── File helpers ──────────────────────────────────────────────────────────────

/** Maximum allowed upload size in bytes (10 MB). */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024

/** Reads a File and resolves to an UploadedFile with its base64 data URL.
 * Rejects with an Error if the file isn't an image, or exceeds {@link MAX_UPLOAD_BYTES}.
 * Images above the shared compression threshold are compressed client-side first (same
 * mechanism used for uploads elsewhere — see `qms-international/qms-trace`). */
export async function fileToUploadedFile(file: File): Promise<UploadedFile> {
  if (!isImageFile(file)) {
    throw new Error(`File "${file.name}" must be an image.`)
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`File "${file.name}" exceeds the 10 MB upload limit.`)
  }
  const compressed = await compressImageFile(file)
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      resolve({ name: file.name, dataUrl: reader.result as string, mimeType: compressed.type })
    reader.onerror = reject
    reader.readAsDataURL(compressed)
  })
}

// ── Google Maps ───────────────────────────────────────────────────────────────

/** Builds a Google Maps search URL for the given address string. */
export function mapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
}

// ── ICS builder ───────────────────────────────────────────────────────────────

function toIcsDate(iso: string): string {
  return iso
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '')
    .replace('Z', 'Z')
}

/** Builds an iCalendar (.ics) Blob describing the event for calendar import. */
export function buildIcs(event: AppEvent): Blob {
  const start = toIcsDate(event.startDateTime)
  const end = event.endDateTime ? toIcsDate(event.endDateTime) : start
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//PROAXIV//EventPlanner//EN',
    'BEGIN:VEVENT',
    `UID:${event.id}@proaxiv.com`,
    `DTSTAMP:${toIcsDate(new Date().toISOString())}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${event.title}`,
    `LOCATION:${event.address}`,
    `DESCRIPTION:${event.text.replace(/\n/g, '\\n')}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ]
  return new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' })
}

/** Generates and triggers a browser download of the event's .ics file. */
export function downloadIcs(event: AppEvent): void {
  const blob = buildIcs(event)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`
  a.click()
  URL.revokeObjectURL(url)
}

// ── Ticket activation ─────────────────────────────────────────────────────────

/**
 * Returns true once the current time has reached the event's ticket activation moment.
 * An empty `ticketActivation` means "visible from the start" (no restriction), so it's active
 * unconditionally — matching the event editor's "QR Code Activation" radio group and the
 * engine's own gating check (`EventParticipantService`: a null `qrActivationDate` never blocks
 * the QR request).
 */
export function isTicketActive(event: AppEvent): boolean {
  if (!event.ticketActivation) return true
  return Date.now() >= Date.parse(event.ticketActivation)
}

// ── Registration open / closed ────────────────────────────────────────────────

/**
 * Returns true when a visitor is allowed to register for the event.
 *
 * Priority:
 *   1. `registrationManualState` — explicit organiser override (beats the deadline).
 *      `'hidden'` behaves like `'closed'`: registration is not allowed.
 *   2. `registrationClosesAt`    — automatic closure once the deadline passes.
 *   3. No fields set             — registration is open by default.
 */
export function isRegistrationOpen(event: AppEvent): boolean {
  if (event.registrationManualState === 'open') return true
  if (event.registrationManualState === 'closed' || event.registrationManualState === 'hidden') {
    return false
  }
  if (!event.registrationClosesAt) return true
  return Date.now() < Date.parse(event.registrationClosesAt)
}

/** Returns true when the registration section should be omitted entirely from the public page. */
export function isRegistrationHidden(event: AppEvent): boolean {
  return event.registrationManualState === 'hidden'
}

// ── Post-event status ─────────────────────────────────────────────────────────

/** Returns true once the current time is at or after the event's start date/time. */
export function hasEventStarted(event: AppEvent): boolean {
  if (!event.startDateTime) return false
  return Date.now() >= Date.parse(event.startDateTime)
}

/**
 * Returns true once the current time is past the event's end date/time.
 * Events with no end date never count as ended (organizer keeps the pre-end tab set).
 */
export function hasEventEnded(event: AppEvent): boolean {
  if (!event.endDateTime) return false
  return Date.now() > Date.parse(event.endDateTime)
}

/**
 * Builds `/e/:id` or `/e/:id/register`, keeping an invitation `email` query param when present so
 * the registration form can stay prefilled across navigation.
 */
export function eventPathWithEmail(
  eventId: string,
  path: '' | '/register',
  email: string | null | undefined
): string {
  const base = `/e/${eventId}${path}`
  const trimmed = email?.trim()
  if (!trimmed) return base
  return `${base}?email=${encodeURIComponent(trimmed)}`
}

/**
 * Builds `/e/:id/register`, pre-filling every field already on file via query params
 * (`email`/`firstName`/`lastName`/`company`/`consentPersonal`/`consentMedia`) — mirrors the
 * backend's `EventParticipantService#registrationUrlWithPrefill`, used for the rejection email's
 * "update & resubmit" link (see #215). Used here for the equivalent in-app action on the
 * ticket page itself, so a rejected participant doesn't need the email to find their way
 * back to a prefilled form, and doesn't have to re-tick consents they already gave.
 */
export function registerPathWithPrefill(
  eventId: string,
  prefill: {
    email?: string | null
    firstName?: string | null
    lastName?: string | null
    company?: string | null
    consentPersonalData?: boolean | null
    consentMedia?: boolean | null
  }
): string {
  const params = new URLSearchParams()
  const email = prefill.email?.trim()
  const firstName = prefill.firstName?.trim()
  const lastName = prefill.lastName?.trim()
  const company = prefill.company?.trim()
  if (email) params.set('email', email)
  if (firstName) params.set('firstName', firstName)
  if (lastName) params.set('lastName', lastName)
  if (company) params.set('company', company)
  // Only carried when true — a consent that wasn't given shouldn't be "pre-declined" either,
  // it should just fall back to the form's normal unchecked default.
  if (prefill.consentPersonalData) params.set('consentPersonal', '1')
  if (prefill.consentMedia) params.set('consentMedia', '1')
  const query = params.toString()
  return `/e/${eventId}/register${query ? `?${query}` : ''}`
}

// ── Font loader (injects a Google Fonts <link> once per font family) ───────────

const loadedFonts = new Set<string>()

/** Injects a Google Fonts stylesheet link for the given family (once per family). */
export function loadGoogleFont(family: string): void {
  if (!family || loadedFonts.has(family)) return
  const option = CURATED_FONTS.find((f) => f.family === family)
  if (!option || !option.query) return // system fonts have no query — nothing to load
  loadedFonts.add(family)
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${option.query}&display=swap`
  document.head.appendChild(link)
}
